import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send } from "lucide-react";
import { api, streamPost } from "@/lib/api";
import { t } from "@/lib/i18n";

const Rich = ({ text }) => text.split(/(\*\*[^*]+\*\*)/g).map((part, k) =>
  part.startsWith("**") && part.endsWith("**") ? <strong key={k}>{part.slice(2, -2)}</strong> : part.replace(/\*\*/g, ""));

const Bubble = ({ role, content, i }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`} data-testid={`coach-msg-${i}`}>
    <div className={`max-w-[85%] rounded-2xl px-5 py-3 leading-relaxed whitespace-pre-line text-[15px] ${role === "user" ? "bg-navy text-white" : "bg-paper text-navy"}`}>
      <Rich text={content} />
    </div>
  </div>
);

export const AiCoach = ({ vid, lang }) => {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);
  const sentRef = useRef(false);

  useEffect(() => { api.get(`/public/results/${vid}/coach`).then((r) => setMsgs(r.data)); }, [vid]);
  useEffect(() => { if (sentRef.current) endRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [msgs]);

  const send = async (text) => {
    const message = text.trim();
    if (!message || busy) return;
    sentRef.current = true;
    setInput("");
    setError("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      await streamPost(`/public/results/${vid}/coach`, { message, lang }, (d) =>
        setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: m[m.length - 1].content + d }]));
    } catch (e) {
      setMsgs((m) => m.slice(0, -1));
      setError(e.message || t(lang, "aiError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card-soft p-8 md:p-10" data-testid="ai-coach">
      <p className="eyebrow flex items-center gap-2"><MessageCircle size={14} /> AI</p>
      <h2 className="font-display text-navy text-3xl font-semibold mt-3">{t(lang, "coachTitle")}</h2>
      <p className="text-muted-ink mt-2">{t(lang, "coachIntro")}</p>
      {msgs.length === 0 && (
        <div className="flex flex-wrap gap-2 mt-6">
          {t(lang, "suggestions").map((s, i) => (
            <button key={s} onClick={() => send(s)} className="chip-btn" data-testid={`coach-suggestion-${i}`}>{s}</button>
          ))}
        </div>
      )}
      <div className="space-y-3 mt-6 max-h-[420px] overflow-y-auto" data-testid="coach-thread">
        {msgs.map((m, i) => <Bubble key={i} i={i} role={m.role} content={m.content || "…"} />)}
        <div ref={endRef} />
      </div>
      {error && <p className="text-sm text-red-700 mt-3" data-testid="coach-error">{error}</p>}
      <form className="flex gap-3 mt-6" onSubmit={(e) => { e.preventDefault(); send(input); }}>
        <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={1000} placeholder={t(lang, "coachPlaceholder")}
          className="flex-1 min-w-0 h-12 rounded-lg border border-line px-4 bg-white text-navy focus:outline-none focus:ring-2 focus:ring-[var(--teal)]" data-testid="coach-input" />
        <button disabled={busy || !input.trim()} className="btn-navy !py-0 h-12 flex items-center gap-2" data-testid="coach-send-btn">
          <Send size={16} /> {t(lang, "send")}
        </button>
      </form>
    </section>
  );
};
